<template>
  <div class="width-100p">
    <v-dialog
      v-model="dialog"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <template #activator="{ on }">
        <v-btn depressed large block color="white" class="ma-0" v-on="on">
          Vault storage settings
        </v-btn>
      </template>

      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11">
              <h2 class="text-h5">Vault storage settings</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />

        <v-card-text class="crud-card-text">
          <p>Date Will signed</p>
          <input
            id="date_will_signed"
            type="date"
            v-model="date_will_signed"
            :max="new Date().toISOString().split('T')[0]"
            required
          />
        </v-card-text>

        <v-card-text class="crud-card-text">
          <v-select
            v-model="nominated_executor_on_paper_will"
            id="nominated_executor_on_paper_will"
            :items="[
              'Old Mutual Will',
              'Old Mutual Wealth Trust',
              'Old Mutual Trust',
              'DEPI',
              'Family/Friend',
            ]"
            label="Nominated Executor on the paper will"
          />
        </v-card-text>

        <v-card-text class="crud-card-text">
          <p>Date stored</p>
          <input
            id="date_stored"
            type="date"
            v-model="date_stored"
            :max="new Date().toISOString().split('T')[0]"
            required
          />
        </v-card-text>

        <v-card-text class="crud-card-text">
          <p>Stored Will Validity</p>
          <ValidationProvider id="willValid" v-slot="{ errors }">
            <v-radio-group v-model="willValid" row :error-messages="errors">
              <v-radio label="Valid" :value="true" color="primary" />
              <v-radio label="Invalid" :value="false" color="primary" />
            </v-radio-group>
          </ValidationProvider>
        </v-card-text>

        <v-card-text v-if="willValid == false" class="crud-card-text" required>
          <v-select
            v-model="willInvalidReason"
            :items="willInvalidReasons"
            item-text="name"
            item-value="value"
            label="Will invalid reason"
          />
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col cols="12">
              <v-btn
                rounded
                depressed
                :loading="buttonLoading"
                color="primary"
                class="left-edit-btn"
                @click="updateVaultStorageSettings()"
              >
                <span>Update</span>
                <v-icon small>{{ icons.mdiArrowRight }}</v-icon>
              </v-btn>

              <v-btn
                class="right-edit-btn"
                rounded
                depressed
                outlined
                color="grey.darken1"
                @click="closeDialog()"
              >
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../../mixins/form-helper-mixin';
  import { UPDATE_TESTATOR_VAULT_STORAGE_SETTINGS_MUTATION } from '../../../constants/graphql/testators';

  export default {
    name: 'vaultStorageSettings',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
      referrerRoute: {
        type: String,
        default: 'AdminPage',
      },
      referrerId: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        dialog: false,
        date_will_signed: this.testator.dateWillSigned,
        nominated_executor_on_paper_will:
          this.testator.nominatedExecutorOnPaperWill,
        date_stored: this.testator.dateStored,
        successMessage: null,
        willValid: this.testator.willValid,
        willInvalidReason: this.testator.willInvalidReason,
        willInvalidReasons: [
          { name: 'Incomplete', value: 'INCOMPLETE' },
          { name: 'Incorrect Template', value: 'INCORRECT_TEMPLATE' },
          { name: 'Witness Conflict', value: 'WITNESS_CONFLICT' },
          { name: 'Not Original Will', value: 'NOT_ORIGINAL_WILL' },
          { name: 'Missing Pages', value: 'MISSING_PAGES' },
          {
            name: 'Signature Inconsistencies',
            value: 'SIGNATURE_INCONSISTENCIES',
          },
        ],
      };
    },
    computed: {
      testatorName() {
        return this.testator.fullName
          ? this.testator.fullName
          : this.testator.email;
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
    },
    methods: {
      updateVaultStorageSettings() {
        const id = this.testator.id;
        const dateWillSignedElement =
          document.getElementById('date_will_signed');
        const nominatedExecutorOnPaperWillElement = document.getElementById(
          'nominated_executor_on_paper_will'
        );
        const dateWillStoredElement = document.getElementById('date_stored');
        const requiredInputs = [
          dateWillSignedElement,
          nominatedExecutorOnPaperWillElement,
          dateWillStoredElement,
        ];

        requiredInputs.forEach(input => {
          !input.value
            ? input.classList.add('error--text')
            : input.classList.remove('error--text');
        });

        if (!this.date_will_signed || !this.date_stored) {
          return;
        }

        const vaultStorageSettingsInput = {
          dateWillSigned: this.date_will_signed,
          nominatedExecutorOnPaperWill: this.nominated_executor_on_paper_will,
          dateStored: this.date_stored,
          willValid: this.willValid,
          willInvalidReason: this.willValid ? null : this.willInvalidReason,
        };

        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_VAULT_STORAGE_SETTINGS_MUTATION,
            variables: {
              id,
              vaultStorageSettingsInput,
            },
          })
          .then(response => {
            if (response.data.updateTestatorVaultStorageSettings) {
              this.successMessage =
                'Vault storage settings successfully updated';
              this.redirect();
            } else {
              this.displayErrorAndCloseDialog();
            }
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      redirect() {
        this.$router.push({
          name: this.referrerRoute,
          params: { id: this.referrerId, successMessage: this.successMessage },
        });
      },
      closeDialog() {
        this.dialog = false;
        this.buttonLoading = false;
      },
    },
  };
</script>
